import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

const authErrorInterceptor = (error: AxiosError<{ status: Number; message?: string }>) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    if (error.response?.data?.message) {
      ElMessage({ type: 'error', message: error.response?.data?.message, duration: 1000 })
    } else if (error.response.statusText) {
      ElMessage({ type: 'error', message: error.response.statusText, duration: 1000 })
    }
    const userStore = useUserStoreWithOut()
    userStore.logout()
  }
  return Promise.reject(error)
}

const exceptionErrorInterceptor = (error: AxiosError<{ status: Number; message?: string }>) => {
  if (error.response?.status === 405) {
    ElMessage({ type: 'error', message: error.response?.data?.message, duration: 1000 })
  }
  return Promise.reject(error)
}

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // You can't do any processing here, otherwise the interceptors later will not get a complete context
    return res
  },
  async (error: AxiosError<{ status: Number; message?: string }>) => {
    const { t } = await import('@/hooks/web/useI18n').then(({ useI18n }) => useI18n())

    authErrorInterceptor(error)
    exceptionErrorInterceptor(error)
    console.log('err： ' + error) // for debug
    ElMessage({ type: 'error', message: t('error.networkError'), duration: 1000 })
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
